import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import useIgnorableError from '@ecp/common/src/hooks/useIgnorableError';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import CheckboxGroupUnstyled from '@ecp/common/src/components/unstyled/checkbox/CheckboxGroupUnstyled';
import LabeledCheckBox from '@ecp/common/src/components/checkbox/bo/LabeledCheckBox';
import { StyledErrorMessage } from '@ecp/common/src/components/input/bo/Input';

const CheckboxGroup = ({ direction, error, errorMessage, onChange, ...props }) => {
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const rootComponent = useMemo(() => (direction === 'row' ? FlexBox : FlexBoxColumn), [direction]);
  const { ignoreError } = useIgnorableError({ error, value: selectedIndexes.sort().join(',') });

  const handleCheckboxChange = useCallback(
    (index, item, event) => {
      setSelectedIndexes((prev) => {
        if (item.checked) {
          !prev.includes(index) && prev.push(index);
          return prev;
        } else {
          return prev.filter((value) => value !== index);
        }
      });
      onChange?.(index, item, event);
    },
    [onChange]
  );

  return (
    <Container>
      <CheckboxGroupUnstyled rootComponent={rootComponent} onChange={handleCheckboxChange} {...props} />
      {!ignoreError && error && errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
    </Container>
  );
};

const Container = styled(FlexBoxColumn)`
  align-self: stretch;
  align-items: flex-start;
  justify-content: center;
`;

const StyledLabeledCheckBox = styled(LabeledCheckBox)`
  height: 28px;
  padding-right: 14px;
`;

CheckboxGroup.propTypes = {
  rootComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  itemComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
  itemList: PropTypes.array.isRequired,
  rootProps: PropTypes.object,
  itemProps: PropTypes.object,
  onChange: PropTypes.func,
  direction: PropTypes.oneOf(['row', 'column']),
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
};

CheckboxGroup.defaultProps = {
  direction: 'row',
  itemComponent: StyledLabeledCheckBox,
  itemList: [],
  rootProps: {},
  itemProps: {},
};

export default CheckboxGroup;
